import { Box, TextField, Switch, FormControlLabel, Grid, Typography, Divider, Button, MenuItem, IconButton, Tooltip, Paper } from '@mui/material';
import { Field, useFormikContext, FieldArray } from 'formik';
import { UserSelect } from '../../../../shared/components/SelectsDinamycs/UserSelect';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { ThemeCustomizationSection } from './ThemeCustomizationSection';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { userService } from '../../../../shared/services/users/usersServices';
import { useQuery } from '@tanstack/react-query';
import { MemoizedQuillEditor } from './MemoizedQuillEditor';
import { MemoizedYouTubePreview } from './MemoizedYouTubePreview';


type EventFormProps = {
  mode: 'create' | 'edit';
  loading?: boolean;
};

export function EventForm({ mode, loading = false }: EventFormProps) {
  const { values, errors, touched, handleChange, setFieldValue } = useFormikContext<any>();
  const [secondaryOrganizers, setSecondaryOrganizers] = useState<Array<{ id: string; name: string }>>([]);

  const secondaryOrganizerIds = useMemo(() => {
    return values.secondary_organizer_ids && Array.isArray(values.secondary_organizer_ids)
      ? values.secondary_organizer_ids
      : [];
  }, [values.secondary_organizer_ids]);

  const idsString = useMemo(() => {
    return [...secondaryOrganizerIds].sort().join(',');
  }, [secondaryOrganizerIds]);

  const { data: secondaryOrganizersData } = useQuery({
    queryKey: ['secondaryOrganizers', idsString],
    queryFn: async () => {
      if (secondaryOrganizerIds.length === 0) return [];
      const promises = secondaryOrganizerIds.map((id: string) => userService.get(id));
      const results = await Promise.all(promises);
      return results.map(res => {
        const user = res.data?.user || res.data;
        return {
          id: user.id,
          name: user.name
        };
      });
    },
    enabled: secondaryOrganizerIds.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false, // Otimização: não refetch se já tem dados
  });

  useEffect(() => {
    if (secondaryOrganizersData && Array.isArray(secondaryOrganizersData) && secondaryOrganizersData.length > 0) {
      const loadedIds = secondaryOrganizersData.map(o => o.id).sort();
      if (loadedIds.join(',') === idsString) {
        setSecondaryOrganizers(secondaryOrganizersData);
      }
    } else if (secondaryOrganizerIds.length === 0) {
      setSecondaryOrganizers([]);
    }
  }, [secondaryOrganizersData, idsString, secondaryOrganizerIds.length]);
  return (
    <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
      <Typography variant="h6" fontWeight={700} color="primary.main" mb={1} mt={1}>
        Dados do Evento
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Field as={TextField} label="Título" name="title" fullWidth required disabled={loading} error={!!(touched.title && errors.title)} helperText={touched.title && errors.title} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field as={TextField} label="Data Início" name="start_date" type="date" fullWidth required disabled={loading} error={!!(touched.start_date && errors.start_date)} helperText={touched.start_date && errors.start_date} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field as={TextField} label="Data Fim" name="end_date" type="date" fullWidth required disabled={loading} error={!!(touched.end_date && errors.end_date)} helperText={touched.end_date && errors.end_date} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field as={TextField} label="Hora Início" name="start_time" type="time" fullWidth disabled={loading} error={!!(touched.start_time && errors.start_time)} helperText={touched.start_time && errors.start_time} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field as={TextField} label="Hora Fim" name="end_time" type="time" fullWidth disabled={loading} error={!!(touched.end_time && errors.end_time)} helperText={touched.end_time && errors.end_time} InputLabelProps={{ shrink: true }} />
        </Grid>

        <Grid item xs={12} md={3}>
          <Field
            as={TextField}
            select
            label="Status"
            name="status"
            fullWidth
            required
            disabled={loading}
            error={!!(touched.status && errors.status)}
            helperText={touched.status && errors.status}
          >
            <MenuItem value="open">Aberto</MenuItem>
            <MenuItem value="closed">Fechado</MenuItem>
            <MenuItem value="cancelled">Cancelado</MenuItem>
          </Field>
        </Grid>
        <Grid item xs={12} md={3}>
          <Field
            as={TextField}
            label="Data limite inscrição"
            name="registration_deadline"
            type="date"
            fullWidth
            required
            disabled={loading}
            error={!!(touched.registration_deadline && errors.registration_deadline)}
            helperText={touched.registration_deadline && errors.registration_deadline}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Campos de personalização da seção de inscrição */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight={600} color="text.primary" mb={1}>
            Personalização da Seção de Inscrição
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Personalize o título e subtítulo exibidos na página pública de inscrição. Deixe em branco para usar os valores padrão.
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Field
            as={TextField}
            label="Título da seção de inscrição"
            name="registration_title"
            fullWidth
            disabled={loading}
            placeholder="Ex: Inscreva-se"
            helperText="Deixe em branco para usar o título padrão: 'Inscreva-se'"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Field
            as={TextField}
            label="Subtítulo da seção de inscrição"
            name="registration_subtitle"
            fullWidth
            multiline
            disabled={loading}
            placeholder="Ex: Preencha seus dados para garantir sua vaga"
            helperText="Deixe em branco para usar o subtítulo padrão"
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight={600} color="text.primary" mb={1}>
            Rastreamento (Google Tag Manager)
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Informe o ID do container GTM deste evento. Ele será carregado apenas nas páginas públicas de inscrição.
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Field
            as={TextField}
            label="Google Tag Manager ID"
            name="gtm_container_id"
            fullWidth
            disabled={loading}
            placeholder="Ex: GTM-XXXXXXX"
            helperText="Deixe em branco para desativar o rastreamento deste evento"
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
            Descrição
          </Typography>
          <Field name="description">
            {({ field, form }: any) => {
              const handleChange = useCallback((value: string) => {
                form.setFieldValue('description', value);
              }, [form]);

              return (
                <MemoizedQuillEditor
                  value={field.value || ''}
                  onChange={handleChange}
                  readOnly={loading}
                  placeholder="Digite a descrição do evento..."
                />
              );
            }}
          </Field>
          {touched.description && errors.description && (
            <Typography color="error" variant="caption">{typeof errors.description === 'string' ? errors.description : ''}</Typography>
          )}
        </Grid>

        {/* Labels dos ingressos */}
        <Grid item xs={12} md={3}>
          <Field as={TextField} type="text" label="Nome do ingresso inteiro" name="label_ingresso_inteiro" fullWidth disabled={loading} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field as={TextField} type="text" label="Nome do ingresso meia" name="label_ingresso_meia" fullWidth disabled={loading} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field as={TextField} type="text" label="Nome do ingresso estudante" name="label_ingresso_estudante" fullWidth disabled={loading} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field as={TextField} type="text" label="Nome do ingresso criança" name="label_ingresso_crianca" fullWidth disabled={loading} />
        </Grid>

        {/* Descrições dos ingressos */}
        <Grid item xs={12} md={3}>
          <Field as={TextField} type="text" label="Descrição do ingresso inteiro" name="description_ingresso_inteiro" fullWidth multiline rows={2} disabled={loading} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field as={TextField} type="text" label="Descrição do ingresso meia" name="description_ingresso_meia" fullWidth multiline rows={2} disabled={loading} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field as={TextField} type="text" label="Descrição do ingresso estudante" name="description_ingresso_estudante" fullWidth multiline rows={2} disabled={loading} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field as={TextField} type="text" label="Descrição do ingresso criança" name="description_ingresso_crianca" fullWidth multiline rows={2} disabled={loading} />
        </Grid>

        <Grid item xs={12} md={3}>
          <Field
            as={TextField}
            label={values.label_ingresso_inteiro || 'Valor Inteiro'}
            name="valor_ingresso_inteiro"
            type="number"
            fullWidth
            required
            disabled={loading}
            error={!!(touched.valor_ingresso_inteiro && errors.valor_ingresso_inteiro)}
            helperText={touched.valor_ingresso_inteiro && errors.valor_ingresso_inteiro}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field
            as={TextField}
            label={values.label_ingresso_meia || 'Valor Meia'}
            name="valor_ingresso_meia"
            type="number"
            fullWidth
            required
            disabled={loading}
            error={!!(touched.valor_ingresso_meia && errors.valor_ingresso_meia)}
            helperText={touched.valor_ingresso_meia && errors.valor_ingresso_meia}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field
            as={TextField}
            label={values.label_ingresso_estudante || 'Valor Estudante'}
            name="valor_ingresso_estudante"
            type="number"
            fullWidth
            required
            disabled={loading}
            error={!!(touched.valor_ingresso_estudante && errors.valor_ingresso_estudante)}
            helperText={touched.valor_ingresso_estudante && errors.valor_ingresso_estudante}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field
            as={TextField}
            label={values.label_ingresso_crianca || 'Valor Criança'}
            name="valor_ingresso_crianca"
            type="number"
            fullWidth
            required
            disabled={loading}
            error={!!(touched.valor_ingresso_crianca && errors.valor_ingresso_crianca)}
            helperText={touched.valor_ingresso_crianca && errors.valor_ingresso_crianca}
          />
        </Grid>

        {/* Controle de Vagas */}
        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" fontWeight={700} color="primary.main" mb={1}>
            Controle de Vagas
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Defina a quantidade máxima de participantes por tipo de ingresso e o limite total do evento.
            Deixe em branco para permitir inscrições ilimitadas.
          </Typography>
        </Grid>

        <Grid item xs={12} md={3}>
          <Field
            as={TextField}
            label={`Máximo ${values.label_ingresso_inteiro || 'Inteiro'}`}
            name="max_participants_inteiro"
            type="number"
            fullWidth
            disabled={loading}
            inputProps={{ min: 0 }}
            helperText="Deixe em branco para ilimitado"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field
            as={TextField}
            label={`Máximo ${values.label_ingresso_meia || 'Meia'}`}
            name="max_participants_meia"
            type="number"
            fullWidth
            disabled={loading}
            inputProps={{ min: 0 }}
            helperText="Deixe em branco para ilimitado"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field
            as={TextField}
            label={`Máximo ${values.label_ingresso_estudante || 'Estudante'}`}
            name="max_participants_estudante"
            type="number"
            fullWidth
            disabled={loading}
            inputProps={{ min: 0 }}
            helperText="Deixe em branco para ilimitado"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Field
            as={TextField}
            label={`Máximo ${values.label_ingresso_crianca || 'Criança'}`}
            name="max_participants_crianca"
            type="number"
            fullWidth
            disabled={loading}
            inputProps={{ min: 0 }}
            helperText="Deixe em branco para ilimitado"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Field
            as={TextField}
            label="Máximo Total de Participantes"
            name="max_participants_total"
            type="number"
            fullWidth
            disabled={loading}
            inputProps={{ min: 0 }}
            helperText="Limite total do evento. Deixe em branco para ilimitado"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
            Imagem do Evento
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={e => {
              if (e.target.files && e.target.files[0]) {
                setFieldValue('image', e.target.files[0]);
              }
            }}
            disabled={loading}
          />



          {typeof values?.image === 'string' && values?.image && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Arquivo atual: {values.image.split('/').pop()}
            </Typography>
          )}
          {touched?.image && errors?.image && (
            <Typography color="error" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              {typeof errors.image === 'string' ? errors.image : ''}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Field
            as={TextField}
            label="URL do vídeo (YouTube)"
            name="video_url"
            type="url"
            fullWidth
            disabled={loading}
            error={!!(touched?.video_url && errors?.video_url)}
            helperText={touched?.video_url && errors?.video_url}
          />

          {values?.video_url && (
            <MemoizedYouTubePreview videoUrl={values.video_url} />
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
            Observação
          </Typography>
          <Field name="observation">
            {({ field, form }: any) => {
              const handleChange = useCallback((value: string) => {
                form.setFieldValue('observation', value);
              }, [form]);

              return (
                <MemoizedQuillEditor
                  value={field.value || ''}
                  onChange={handleChange}
                  readOnly={loading}
                  placeholder="Digite observações sobre o evento..."
                />
              );
            }}
          </Field>
          {touched.observation && errors.observation && (
            <Typography color="error" variant="caption">{typeof errors.observation === 'string' ? errors.observation : ''}</Typography>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
            Regras para Crianças
          </Typography>
          <Field name="rules_for_children">
            {({ field, form }: any) => {
              const handleChange = useCallback((value: string) => {
                form.setFieldValue('rules_for_children', value);
              }, [form]);

              return (
                <MemoizedQuillEditor
                  value={field.value || ''}
                  onChange={handleChange}
                  readOnly={loading}
                  placeholder="Digite as regras para crianças..."
                />
              );
            }}
          </Field>
          {touched.rules_for_children && errors.rules_for_children && (
            <Typography color="error" variant="caption">{typeof errors.rules_for_children === 'string' ? errors.rules_for_children : ''}</Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <UserSelect
            value={values.organizer_id ? { id: values.organizer_id, name: values.organizer_name || '' } : null}
            onChange={user => {
              setFieldValue('organizer_id', Array.isArray(user) ? '' : user?.id || '');
              setFieldValue('organizer_name', Array.isArray(user) ? '' : user?.name || '');
            }}
            label="Organizador Principal"
            filter={{ role: 'organizer' }}
            excludeIds={secondaryOrganizerIds}
          />
          {touched.organizer_id && errors.organizer_id && (
            <Typography color="error" variant="caption">{typeof errors.organizer_id === 'string' ? errors.organizer_id : ''}</Typography>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <UserSelect
            multiple
            value={secondaryOrganizers.length > 0 ? secondaryOrganizers : []}
            onChange={users => {
              const selectedUsers = Array.isArray(users) ? users : [];
              const ids = selectedUsers.map(u => u.id);
              setFieldValue('secondary_organizer_ids', ids);
              if (selectedUsers.length > 0 && selectedUsers.every(u => u.id && u.name)) {
                setSecondaryOrganizers(selectedUsers);
              }
            }}
            label="Organizadores Secundários (Opcional)"
            filter={{ role: 'organizer' }}
            placeholder="Selecione organizadores secundários..."
            excludeIds={values.organizer_id ? [values.organizer_id] : []}
          />
          {touched.secondary_organizer_ids && errors.secondary_organizer_ids && (
            <Typography color="error" variant="caption">{typeof errors.secondary_organizer_ids === 'string' ? errors.secondary_organizer_ids : ''}</Typography>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                name="show_organizer_email"
                checked={values.show_organizer_email !== undefined ? values.show_organizer_email : true}
                onChange={(e) => setFieldValue('show_organizer_email', e.target.checked)}
                disabled={loading}
              />
            }
            label="Exibir email do organizador na página do participante"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                name="show_organizer_phone"
                checked={values.show_organizer_phone !== undefined ? values.show_organizer_phone : true}
                onChange={(e) => setFieldValue('show_organizer_phone', e.target.checked)}
                disabled={loading}
              />
            }
            label="Exibir telefone do organizador na página do participante"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Switch
                name="overnight"
                checked={!!values.overnight}
                onChange={(e) => setFieldValue('overnight', e.target.checked)}
                disabled={loading}
              />
            }
            label="Pernoite"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Switch
                name="own_transport"
                checked={!!values.own_transport}
                onChange={(e) => setFieldValue('own_transport', e.target.checked)}
                disabled={loading}
              />
            }
            label="Transporte Próprio"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Field
                as={Switch}
                name="hasTickets"
                checked={values.hasTickets}
                onChange={(e: any) => {
                  const hasTickets = e.target.checked;
                  setFieldValue('hasTickets', hasTickets);

                  setFieldValue('requireCheckIn', hasTickets);
                }}
                disabled={loading || mode === 'edit'}
              />
            }
            label="Evento com Ingressos"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1 }}>
            Este campo só pode ser definido na criação do evento
          </Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Field
                as={Switch}
                name="requireCheckIn"
                checked={values.requireCheckIn}
                onChange={handleChange}
                disabled={true}
              />
            }
            label="Requer Check-in"
          />
        </Grid>
      </Grid>

      {/* Métodos de Pagamento */}
      <Typography variant="h6" fontWeight={700} color="primary.main" mb={1} mt={4}>
        Métodos de Pagamento
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="body2" color="text.secondary" mb={2}>
        Selecione quais formas de pagamento estarão disponíveis para os participantes
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Field
                as={Switch}
                name="payment_pix"
                checked={values.payment_pix ?? true}
                onChange={(e: any) => setFieldValue('payment_pix', e.target.checked)}
                disabled={loading}
              />
            }
            label="PIX"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Field
                as={Switch}
                name="payment_card"
                checked={values.payment_card ?? true}
                onChange={(e: any) => setFieldValue('payment_card', e.target.checked)}
                disabled={loading}
              />
            }
            label="Cartão de Crédito"
          />
        </Grid>
        {<Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Field
                as={Switch}
                name="payment_boleto"
                checked={values.payment_boleto ?? true}
                onChange={(e: any) => setFieldValue('payment_boleto', e.target.checked)}
                disabled={loading}
                defaultChecked={false}
              />
            }
            label="Boleto"
          />
        </Grid>}
      </Grid>

      {/* Taxas e Juros - Só exibe se algum método de pagamento estiver habilitado */}
      {(values.payment_boleto || values.payment_card) && (
        <>
          <Typography variant="h6" fontWeight={700} color="primary.main" mb={1} mt={4}>
            Taxas e Juros
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            {/* Taxas do Boleto - Só exibe se boleto estiver habilitado */}
            {values.payment_boleto && (
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 2, border: '1px solid', borderColor: 'warning.200' }}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1} color="warning.dark">
                    📄 Boleto Bancário
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Taxas aplicadas em caso de atraso no pagamento do boleto
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="boleto_interest"
                        label="Juros ao mês"
                        type="number"
                        fullWidth
                        size="small"
                        inputProps={{ min: 0, max: 10, step: 0.1 }}
                        InputProps={{
                          endAdornment: <Typography variant="body2" color="text.secondary">%</Typography>
                        }}
                        helperText="Ex: 1% = R$10/mês para cada R$1.000"
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="boleto_fine"
                        label="Multa por atraso"
                        type="number"
                        fullWidth
                        size="small"
                        inputProps={{ min: 0, max: 10, step: 0.1 }}
                        InputProps={{
                          endAdornment: <Typography variant="body2" color="text.secondary">%</Typography>
                        }}
                        helperText="Ex: 2% = R$20 para cada R$1.000"
                        disabled={loading}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}

            {/* Taxas do Cartão - Só exibe se cartão estiver habilitado */}
            {values.payment_card && (
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 2, border: '1px solid', borderColor: 'warning.200' }}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1} color="warning.dark">
                    💳 Cartão de Crédito
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Taxa adicional repassada ao cliente no pagamento via cartão
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="card_fee"
                        label="Taxa fixa"
                        type="number"
                        fullWidth
                        size="small"
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>R$</Typography>
                        }}
                        helperText="Valor fixo adicionado ao total"
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="card_fee_percent"
                        label="Taxa percentual"
                        type="number"
                        fullWidth
                        size="small"
                        inputProps={{ min: 0, max: 20, step: 0.1 }}
                        InputProps={{
                          endAdornment: <Typography variant="body2" color="text.secondary">%</Typography>
                        }}
                        helperText="Ex: 5% de R$100 = R$5"
                        disabled={loading}
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="warning.main" display="block" mt={1}>
                    ⚠️ O cliente verá: Ingresso + Taxa = Total
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Máximo de Parcelas */}
          <Typography variant="h6" fontWeight={700} color="primary.main" mb={1} mt={4}>
            Máximo de Parcelas
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" mb={2}>
            Defina o número máximo de parcelas permitidas para cada método de pagamento
          </Typography>

          <Grid container spacing={3}>
            {values.payment_card && (
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  name="max_installments_card"
                  label="Máximo de parcelas (Cartão)"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1, max: 12, step: 1 }}
                  helperText="Máximo de parcelas permitidas no cartão de crédito (padrão: 10)"
                  disabled={loading}
                />
              </Grid>
            )}
            {values.payment_boleto && (
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  name="max_installments_boleto"
                  label="Máximo de parcelas (Boleto)"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1, max: 12, step: 1 }}
                  helperText="Máximo de parcelas permitidas no boleto (padrão: 12). Cada parcela precisa de 30 dias para vencimento."
                  disabled={loading}
                />
              </Grid>
            )}
          </Grid>
        </>
      )}

      {/* Campos Dinâmicos */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight={700} color="primary.main" mb={1}>
          Campos Dinâmicos
        </Typography>
        <FieldArray name="dynamicFields">
          {({ push, remove, form, move }) => (
            <Box>
              {form.values.dynamicFields?.map((field: any, idx: number) => (
                <Paper
                  key={idx}
                  elevation={1}
                  sx={{
                    mt: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    {/* Campo oculto para preservar o ID */}
                    {field?.id && <Field type="hidden" name={`dynamicFields.${idx}.id`} />}

                    {/* Controles de ordenação - lado esquerdo */}
                    <Grid item xs={12} md={1}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                          p: 1,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Tooltip title="Mover para cima" arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => move(idx, idx - 1)}
                              disabled={idx === 0}
                              sx={{
                                bgcolor: idx === 0 ? 'transparent' : 'white',
                                '&:hover': {
                                  bgcolor: idx === 0 ? 'transparent' : 'primary.50',
                                },
                                '&:disabled': {
                                  opacity: 0.3,
                                },
                              }}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Box
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            px: 1,
                            py: 0.5,
                            bgcolor: 'white',
                            borderRadius: 1,
                            minWidth: 24,
                            textAlign: 'center',
                          }}
                        >
                          {idx + 1}
                        </Box>
                        <Tooltip title="Mover para baixo" arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => move(idx, idx + 1)}
                              disabled={idx === form.values.dynamicFields.length - 1}
                              sx={{
                                bgcolor: idx === form.values.dynamicFields.length - 1 ? 'transparent' : 'white',
                                '&:hover': {
                                  bgcolor: idx === form.values.dynamicFields.length - 1 ? 'transparent' : 'primary.50',
                                },
                                '&:disabled': {
                                  opacity: 0.3,
                                },
                              }}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Field as={TextField} label="Label" name={`dynamicFields.${idx}.label`} fullWidth required />
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                      <Field as={TextField} label="Placeholder" name={`dynamicFields.${idx}.placeholder`} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                      <Field as={TextField} select label="Tipo" name={`dynamicFields.${idx}.type`} fullWidth required>
                        <MenuItem value="text">Texto</MenuItem>
                        <MenuItem value="yes_no">Sim/Não</MenuItem>
                        <MenuItem value="date">Data</MenuItem>
                        <MenuItem value="email">Email</MenuItem>
                        <MenuItem value="phone">Telefone</MenuItem>
                        <MenuItem value="select">Seleção</MenuItem>
                      </Field>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControlLabel
                        control={
                          <Field as={Switch} name={`dynamicFields.${idx}.required`} checked={field.required} />
                        }
                        label="Obrigatório"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => remove(idx)}
                        fullWidth
                        sx={{ mt: { xs: 1, md: 0 } }}
                      >
                        Remover
                      </Button>
                    </Grid>

                    {field.type === 'select' && (
                      <Grid item xs={12} md={12}>
                        <TextField
                          label="Opções (separe por vírgula, ex: P,M,G,GG)"
                          name={`dynamicFields.${idx}.optionsString`}
                          fullWidth
                          required
                          value={field.optionsString ?? (field.options ? field.options.join(',') : '')}
                          onChange={e => {
                            const value = e.target.value;
                            const options = value.split(',').map((opt: string) => opt.trim()).filter(Boolean);
                            form.setFieldValue(`dynamicFields.${idx}.options`, options);
                            form.setFieldValue(`dynamicFields.${idx}.optionsString`, value);
                          }}
                          helperText="Obrigatório para campos do tipo Seleção"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}
              <Button variant="contained" sx={{ mt: 2 }} onClick={() => push({ label: '', type: 'text', required: false })}>
                Adicionar Campo
              </Button>
            </Box>
          )}
        </FieldArray>
      </Box>


      <Box sx={{ mt: 4 }}>
        <ThemeCustomizationSection />
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight={700} color="primary.main" mb={1}>
          Souvenirs
        </Typography>
        <FieldArray name="souvenirs" >
          {({ push, remove, form }) => (
            <Box >
              {form.values.souvenirs?.map((_souvenir: any, idx: number) => (
                <Grid container sx={{ mt: 2 }} spacing={2} key={idx} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Field as={TextField} label="Título" name={`souvenirs.${idx}.title`} fullWidth required />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Field as={TextField} label="Descrição" name={`souvenirs.${idx}.description`} fullWidth required />
                  </Grid>
                  {/* <Grid item xs={12} md={2}>
                    <Field as={TextField} label="Imagem (URL)" name={`souvenirs.${idx}.image`} fullWidth />
                  </Grid> */}
                  <Grid item xs={12} md={1}>
                    <Button variant="outlined" color="error" onClick={() => remove(idx)}>
                      Remover
                    </Button>
                  </Grid>
                </Grid>
              ))}
              <Button variant="contained" sx={{ mt: 2 }} onClick={() => push({ title: '', description: '', image: '' })}>
                Adicionar Souvenir
              </Button>
            </Box>
          )}
        </FieldArray>
      </Box>
    </Box>
  );
}